import { Text } from "@mantine/core";
import React from "react";
interface EntryGridRowProps {
  children: React.ReactNode;
  style?: React.CSSProperties;
}
interface OrderEntryRowProps {
  orderStage?: number;
}

//#region Payment Grid Definitions
export const PaymentGridRow: React.FC<EntryGridRowProps> = ({
  children,
  style,
}) => {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "0.5fr 7fr 0.5fr 0.5fr 0.5fr auto",
        gap: "8px",
        marginBottom: "2px",
        width: "100%",
        ...style,
      }}
    >
      {children}
    </div>
  );
};
export const PaymentHeaderRow: React.FC = () => {
  return (
    <PaymentGridRow>
      <Text ta={"left"} fw={600} size="md" style={{ flexGrow: 1 }}>
        Invoice #
      </Text>
      <Text ta={"left"} fw={600} style={{ flexGrow: 1 }} size="md">
        Debtor Name
      </Text>
      <Text ta={"left"} fw={600} style={{ flexGrow: 1 }} size="md">
        Expected
      </Text>
      <Text ta={"left"} fw={600} style={{ flexGrow: 1 }} size="md">
        Original $
      </Text>
      <Text
        title="Deductions"
        ta={"left"}
        fw={600}
        style={{ flexGrow: 1 }}
        size="md"
      >
        Deductions
      </Text>
      <Text ta={"left"} fw={600} style={{ width: 24 }} size="md" />
    </PaymentGridRow>
  );
};
//#endregion

//#region Credit Note Grid Definitions
export const CreditNoteGridRow: React.FC<EntryGridRowProps> = ({
  children,
  style,
}) => {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 3fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr auto",
        gap: "8px",
        marginBottom: "2px",
        width: "100%",
        ...style,
      }}
    >
      {children}
    </div>
  );
};
export const CreditNoteHeaderRow: React.FC = () => {
  return (
    <CreditNoteGridRow>
      <Text ta={"left"} fw={600} size="md" style={{ flexGrow: 1 }}>
        Product #
      </Text>
      <Text ta={"left"} fw={600} style={{ flexGrow: 1 }} size="md">
        Description
      </Text>
      <Text ta={"left"} fw={600} style={{ flexGrow: 1 }} size="md">
        E/Price
      </Text>
      <Text ta={"left"} fw={600} style={{ flexGrow: 1 }} size="md">
        C/Price
      </Text>
      <Text ta={"left"} fw={600} style={{ flexGrow: 1 }} size="md">
        U/CTN
      </Text>
      <Text ta={"left"} fw={600} style={{ flexGrow: 1 }} size="md">
        Ordered
      </Text>
      <Text ta={"left"} fw={600} style={{ flexGrow: 1 }} size="md">
        Supplied
      </Text>
      <Text ta={"left"} fw={600} style={{ flexGrow: 1 }} size="md">
        Returned
      </Text>
      <Text ta={"left"} fw={600} style={{ flexGrow: 1 }} size="md">
        Disc %
      </Text>
      <Text ta={"left"} fw={600} style={{ flexGrow: 1 }} size="md">
        Return Type
      </Text>
      <Text ta={"left"} fw={600} style={{ width: 24 }} size="md" />
    </CreditNoteGridRow>
  );
};
//#endregion
//#region Journal Grid Definitions
export const JournalEntryGridRow: React.FC<EntryGridRowProps> = ({
  children,
  style,
}) => {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 10fr 1fr auto",
        gap: "8px",
        marginBottom: "8px",
        width: "100%",
        ...style,
      }}
    >
      {children}
    </div>
  );
};
export const JournalEntryHeaderRow: React.FC = () => {
  return (
    <JournalEntryGridRow>
      <Text ta={"left"} fw={600} style={{ flexGrow: 1 }} size="md">
        Invoice #
      </Text>
      <Text ta={"left"} fw={600} style={{ flexGrow: 1 }} size="md">
        Debtor Name
      </Text>
      <Text ta={"left"} fw={600} style={{ flexGrow: 1 }} size="md">
        Amount
      </Text>
      <Text fw={600} size="md" style={{ width: 24 }} />
    </JournalEntryGridRow>
  );
};
//#endregion
//#region Admin Order Grid Definitions
export const OrderEntryGridRow: React.FC<
  EntryGridRowProps & OrderEntryRowProps
> = ({ children, style, orderStage }) => {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns:
          (orderStage ?? 0) === 0
            ? "1fr 1fr 8fr 1fr 1fr 1fr 1fr auto"
            : "1fr 1fr 8fr 1fr 1fr 1fr 1fr 1fr 1fr auto",
        gap: "8px",
        marginBottom: "8px",
        width: "100%",
        minWidth: 0, // ← REQUIRED

        alignItems: "center", // 👈 ensures inputs and text align vertically
        ...style,
      }}
    >
      {children}
    </div>
  );
};
export const OrderEntryHeaderRow: React.FC<OrderEntryRowProps> = ({
  orderStage = 0,
}) => {
  return (
    <OrderEntryGridRow {...{ orderStage }}>
      <Text ta={"left"} fw={600} style={{ flexGrow: 1 }} size="md">
        Product #
      </Text>
      <Text fw={600} style={{ marginLeft: orderStage === 0 ? "8px" : "0" }}>
        Units
      </Text>
      <Text fw={600} style={{ marginLeft: orderStage === 0 ? "12px" : "22px" }}>
        Description
      </Text>

      <Text
        ta={"left"}
        fw={600}
        style={{ marginLeft: orderStage === 0 ? "28px" : "26px" }}
        size="md"
      >
        Ord
      </Text>
      <Text
        ta={"left"}
        fw={600}
        style={{ marginLeft: orderStage === 0 ? "30px" : "30px" }}
        size="md"
      >
        Sup
      </Text>
      {(orderStage ?? 0) !== 0 && (
        <>
          <Text ta={"left"} fw={600} style={{ marginLeft: "32px" }}>
            Price
          </Text>
          <Text ta={"left"} fw={600} style={{ marginLeft: "32px" }}>
            Dis %
          </Text>
        </>
      )}

      <Text ta={"left"} fw={600} style={{ marginLeft: "32px" }} size="md">
        Ref
      </Text>
      <Text ta={"left"} fw={600} style={{ marginLeft: "32px" }} size="md">
        B/Order?
      </Text>
      <Text ta={"left"} fw={600} style={{ marginLeft: "12px" }} size="md">
        Actions
      </Text>
    </OrderEntryGridRow>
  );
};
//#endregion
